/* eslint-disable @typescript-eslint/no-explicit-any */
import { ACTION_CREDENTIALS_EXPOSED_DIALOG_DISMISS } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Checkbox, Link, Typography } from 'omakase';
import { CreateAccountWithProfileRequest } from 'actions/AppActions';
import { Head } from 'head/Head';

import { Network, OAuthProfile } from 'components/OAuth';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { SOFT_OPTIN_PROMPT } from '@tock/shared/hooks/useDataPrivacy';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { clearCsrfInterval, setupCsrfTokenRefresh } from '@tock/shared/utils/csrfTokenUtils';
import { core, safeToNumber, useAppContext } from '@tock/coretypes';
import { isSafeContinuePath } from '@tock/shared/utils/hrefValidate';
import { makeStyles } from '@material-ui/core/styles';
import { refreshCalendar } from 'utils/refreshData';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useSelector } from 'react-redux';
import { useWillUnmountEffect } from '@tock/hooks';
import Anchor from 'components/Anchor';
import ConsumerOverlay from 'components/ConsumerOverlay';
import CredentialsExposedDialog from 'components/credentials-exposed/CredentialsExposedDialog';
import FocusedPanelSeparator from 'components/focused-panel/FocusedPanelSeparator';
import LoginForm from 'forms/LoginForm';
import React, { useEffect, useState } from 'react';
import SignupForm from 'forms/SignupForm';
import SocialLoginButtons from 'components/SocialLoginButtons';
import VerifyEmailDialog from 'components/verify-email/VerifyEmailDialog';
import Wrapper from 'components/Wrapper';
import _ from 'lodash';
import clsx from 'clsx';
import queryString, { parse } from '@tock/query-string';
import signinStyles from './signinStyles';
import useConsumerMarketingOptIn from 'hooks/useConsumerMarketingOptIn';

interface GetHeadingOptions {
  isCheckout?: boolean;
  isAccountActivation?: boolean;
  headingOverrides?: { logInOverride: string; signUpOverride: string };
}

interface Tab {
  action: 'Sign-up' | 'Log-in';
  getHeading(options: GetHeadingOptions): string;
}
// exported for testing
export const loginTab: Tab = {
  action: 'Log-in',
  getHeading: ({ headingOverrides }) => {
    return headingOverrides?.logInOverride ?? 'Log in to continue';
  },
};

// exported for testing
export const signupTab: Tab = {
  action: 'Sign-up',
  getHeading: ({ headingOverrides, isCheckout, isAccountActivation }: GetHeadingOptions) => {
    if (headingOverrides) {
      return headingOverrides.signUpOverride;
    }

    if (isCheckout) {
      return 'Add your reservation information';
    }

    if (isAccountActivation) {
      return 'Finish account setup';
    }

    return 'Sign up to continue';
  },
};

const tabs: { [index: string]: Tab } = {
  login: loginTab,
  signup: signupTab,
};

export type SignInMode = 'login' | 'signup';

const askWindowSentinel = {
  askTheWindowObject: true,
};

type AskWindowSentinel = typeof askWindowSentinel;

export function makeSigninLink(
  loginOrSignup: 'login' | 'signup',
  location:
    | string
    | RouteComponentProps['location']
    | Location
    | AskWindowSentinel = askWindowSentinel
) {
  return makeSigninLinkImpl(
    loginOrSignup,
    typeof location === 'string' || 'askTheWindowObject' in location ? location : location.pathname,
    ''
  );
}

export function makeSigninLinkIncludingSearch(
  loginOrSignup: 'login' | 'signup',
  location: RouteComponentProps['location'] | Location | AskWindowSentinel = askWindowSentinel
) {
  return 'askTheWindowObject' in location
    ? makeSigninLinkImpl(loginOrSignup, location, location)
    : makeSigninLinkImpl(loginOrSignup, location.pathname, location.search);
}

/** */
export function makeSigninLinkImpl(
  loginOrSignup: 'login' | 'signup',
  pathname: string | AskWindowSentinel,
  search: string | AskWindowSentinel
) {
  if (typeof pathname !== 'string') {
    // ask the window.location for the pathname
    if (window) {
      ({ pathname } = window.location);
    } else {
      // as a failsafe:
      return '/';
    }
  }

  if (typeof search !== 'string') {
    // ask window.location for the pathname, default to ''
    search = window?.location.search ?? '';
  }

  return `/${loginOrSignup}?continue=${encodeURIComponent(pathname + search)}`;
}
export interface SigninProps {
  hideHeader?: boolean | 'onmobileonly';
  initialGuest?: core.Patron;
  /**
   * should be true when a user needs to finish account setup, shows sign up section only
   */
  isAccountActivation?: boolean;
  isCheckout?: boolean;
  isWineShop?: boolean;
  /**
   * Specify this to populate the page title displayed in the header. It will apply in all circumstances.
   */
  helmetHeadAlreadyPresent?: boolean;
  headingOverrides?: { logInOverride: string; signUpOverride: string };
  /**
   * show the login section only
   */
  isLoginOnly?: boolean;
  onAuthComplete?(tab: SignInMode): void;
  onChangeTab?(newTab: SignInMode): void;
  postSigninPath?: string;
  submitText?: string;
  tab: SignInMode;
}

const useStyles = makeStyles(signinStyles);

export interface LoginValues {
  email: string;
  password: string;
}

export interface SignupValues {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  phoneCountryCode: string;
  isoCountryCode?: string;
  zipCode?: string;
  smsPhoneConsentStatus: core.SmsPhone.StatusType;
  challengeResponse?: string;
}

const Signin: React.FunctionComponent<SigninProps> = ({
  hideHeader,
  headingOverrides,
  helmetHeadAlreadyPresent,
  initialGuest,
  isAccountActivation,
  isCheckout,
  isWineShop,
  isLoginOnly,
  onAuthComplete,
  onChangeTab,
  postSigninPath,
  submitText,
  tab,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const { actions } = useActions();
  const location = useLocation();
  const [showVerifyEmailDialog, setShowVerifyEmailDialog] = useState(false);
  const [showCredentialsExposedDialog, setShowCredentialsExposedDialog] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const noSessionForUnverifiedPatron = useFeatureFlag('NO_SESSION_FOR_UNVERIFIED_PATRON');
  const history = useHistory();
  const { getPath, replaceRouteWith } = useAppContext();
  const { softOptIn, setSoftOptIn, showSoftOptInCheckbox, marketingOptInStatus } =
    useConsumerMarketingOptIn();
  const replaceRouteWithFullReload = (href: string) => {
    window.location.assign(href);
  };
  const hasEnableCsrfToken = useFeatureFlag('ENABLE_CSRF_TOKEN');
  const csrfInterval = React.useRef<NodeJS.Timeout | null>(null);

  const { ticketTypeAccessToken, config, isLoading, lastGuest, detectedLocation } = useSelector(
    (state: StoreType) => ({
      ticketTypeAccessToken: state.calendar.ticketTypeAccessToken,
      config: state.app.config,
      chaseUrContext: state.app.chaseUrContext,
      isLoading: state.app.requestInProgress,
      lastGuest: state.lastGuest.guest,
      detectedLocation: state.app.detectedLocation,
    })
  );

  const styles = useStyles();

  // We do this strange rebuilding of headingOverrides so that the useEffect(changeTab, ...)
  // below can get an honest accounting of its deps array. If we put headingOverrides in the
  // deps array directly, this will cause infinite render cycles in a lot of common usage
  // patterns. (e.g., where a caller specifies headingOverrides via an inline object without
  // declaring it as an externalized constant or memoizing it.)
  const logInOverride = headingOverrides?.logInOverride;
  const signUpOverride = headingOverrides?.signUpOverride;

  const changeTab = () => {
    const currentTab = tabs[tab];

    const headingOverridesRebuilt =
      logInOverride !== undefined && signUpOverride !== undefined
        ? {
            logInOverride,
            signUpOverride,
          }
        : undefined;

    actions.navigation.set({
      hasLogo: false,
      hasBack: true,
      title: currentTab.getHeading({
        headingOverrides: headingOverridesRebuilt,
        isCheckout,
        isAccountActivation,
      }),
    });
    actions.checkout.setCheckoutStatus(currentTab.getHeading({ isCheckout, isAccountActivation }));
  };

  useEffect(changeTab, [
    actions.checkout,
    actions.navigation,
    isAccountActivation,
    logInOverride,
    signUpOverride,
    isCheckout,
    tab,
  ]);

  useWillUnmountEffect(() => {
    clearCsrfInterval(csrfInterval);
  });

  const trackAuthEventInternal = (
    event: 'Login' | 'Signup',
    patron?: core.IPatron,
    options?: { network?: Network }
  ) => {
    window?.analytics?.sendAuthEvent(event, patron, options);
  };

  const wrappedOnChangeTab = React.useCallback(
    (newTab: SignInMode) => {
      if (onChangeTab) {
        onChangeTab(newTab);
      } else {
        history.replace({
          pathname: newTab,
          search: location.search,
        });
      }
      // checking first, handles jest
      if (window.analytics) {
        window.analytics.sendEvent('tabs', 'click', 'toggleLoginSignupTab');
      }
    },
    [onChangeTab, history, location]
  );

  const handleChangeTabsLogin = React.useCallback(
    () => wrappedOnChangeTab('login'),
    [wrappedOnChangeTab]
  );
  const handleChangeTabsSignup = React.useCallback(
    () => wrappedOnChangeTab('signup'),
    [wrappedOnChangeTab]
  );

  const handlePostAuth = async (
    oauthProfile?: OAuthProfile | ProtoResponse<core.PatronProfile>
  ): Promise<ProtoResponse<core.PatronProfile>> => {
    // Ensure CSRF token is set before doing any other requests
    await setupCsrfTokenRefresh(actions.csrfTokenActions.getCsrfToken, hasEnableCsrfToken, true, {
      current: csrfInterval.current,
    });

    const [_, patronProfileResponse] = await Promise.all([
      actions.tockGiftCard.checkTockGiftCardBalance(),
      actions.app.fetchPatronProfile(),
    ]);

    return await updateProfileIfNecessary(patronProfileResponse, oauthProfile as OAuthProfile);
  };

  const updateProfileIfNecessary = (
    response: ProtoResponse<core.PatronProfile>,
    oauthProfile?: OAuthProfile
  ) => {
    const profile = response.payload;
    let needsUpdate = false;

    // Update social profile info if any required fields are missing
    // This will backfill data that might be missing in certain social auth flows.
    if (profile.patron) {
      if (!profile.patron.firstName && !!oauthProfile?.firstName) {
        profile.patron.firstName = oauthProfile?.firstName;
        needsUpdate = true;
      }

      if (!profile.patron.lastName && !!oauthProfile?.lastName) {
        profile.patron.lastName = oauthProfile?.lastName;
        needsUpdate = true;
      }

      if (!profile.patron.email && !!oauthProfile?.email) {
        profile.patron.email = oauthProfile?.email;
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      return actions.app.updatePatronProfile(profile, false);
    } else {
      return Promise.resolve<ProtoResponse<core.PatronProfile>>(response);
    }
  };

  const handleResendVerificationEmail = (email: string) => {
    actions.app.resendVerificationEmail(email);
  };

  const handleAuthComplete = () => {
    window.analytics.sendEvent('Sign-up / Log-in', 'Log-in', 'Tock');
    handlePostAuth().then(({ payload: { patron } }) => {
      trackAuthEventInternal('Login', patron || undefined);
      onAuthComplete ? onAuthComplete(tab) : replaceRouteWithFullReload(getNextPath());
      if (config?.business?.id) {
        setTimeout(() => {
          refreshCalendar(actions, ticketTypeAccessToken);
        });
      }
    });
  };

  const handleLoginWithPassword = (formValues: LoginValues) => {
    const { email, password } = formValues;
    setUserEmail(email);
    const loginPromise = actions.app.login(email, password);

    return new Promise<void>((_, reject) => {
      loginPromise
        .then((response) => {
          const credentialsExposed = response.payload.credentialsExposed;
          if (credentialsExposed) {
            setShowCredentialsExposedDialog(true);
            return;
          }
          handleAuthComplete();
        })
        .catch((response) => {
          if (safeToNumber(response.error.errorCode) === 1001) {
            const checkout = isCheckout ? '/checkout' : '';
            // fixme: our getPath function is weird, not good type safety.
            const url = getPath(
              `${checkout}/signup?email=` + encodeURIComponent(`${email}`)
            ) as string;
            const message = (
              <span>
                No account was found with that email. Would you like to{' '}
                <Anchor href={url}>create one</Anchor>?
              </span>
            );
            reject({ _error: message });
          } else if (safeToNumber(response.error.errorCode) === 5029) {
            const message = (
              <span>
                To finish setting up your account, please click on the link in the email we sent
                you.
                <Typography
                  color="fgPrimaryBase"
                  variant="body02"
                  css={css`
                    cursor: pointer;
                  `}
                  component="a"
                  onClick={() => handleResendVerificationEmail(email)}
                >
                  {' Resend email'}
                </Typography>
              </span>
            );
            reject({ _error: message });
          } else if (response.error.message) {
            reject({ _error: response.error.message });
          }
        });
    });
  };

  const onHandleSignupComplete = () => {
    if (!noSessionForUnverifiedPatron) {
      onAuthComplete ? onAuthComplete(tab) : replaceRouteWithFullReload(getNextPath());
    } else {
      setShowVerifyEmailDialog(true);
    }
  };

  const onEmailVerificationClose = () => {
    setShowVerifyEmailDialog(false);
    handleChangeTabsLogin();
  };

  const handleSignup = async (
    formValues: SignupValues,
    phone: string,
    requireChallengeResponse: boolean
  ) => {
    const sharedAuthRequestPieces = {
      ...formValues,
      phone,
      tockB2cMarketingOptInStatus: marketingOptInStatus,
    };
    const authRequest: CreateAccountWithProfileRequest = {
      ...sharedAuthRequestPieces,
      authProvider: 'PASSWORD' as core.AuthProviders.ProviderType,
    };
    if (isAccountActivation) {
      authRequest.context = 'ACCOUNT_ACTIVATION';
    }
    const { firstName, lastName, email, password, zipCode } = formValues;
    setUserEmail(email);
    try {
      await actions.app.createAccountWithProfile(authRequest, requireChallengeResponse);
      window.analytics.sendEvent('Sign-up / Log-in', 'Sign-up', 'Tock');
      if (!noSessionForUnverifiedPatron) {
        const {
          payload: { patron },
        } = await handlePostAuth();
        trackAuthEventInternal('Signup', patron || undefined);
      }
      onHandleSignupComplete();
      window.dataLayer.push({
        event: 'registration',
        newAccountEmail: email,
        newAccountFirstName: firstName,
        newAccountLastName: lastName,
        newAccountZipCode: zipCode,
      });
      return undefined;
    } catch (error: any) {
      // if account already exist, attempt to log them in
      if (error.error && safeToNumber(error.error.errorCode) === 2010) {
        try {
          await actions.app.login(email, password);
          const {
            payload: { patron },
          } = await handlePostAuth();
          trackAuthEventInternal('Login', patron || undefined);
          onAuthComplete ? onAuthComplete(tab) : replaceRouteWithFullReload(getNextPath());
          return undefined;
        } catch (error: any) {
          if (error.error && safeToNumber(error.error.errorCode) === 5001) {
            return handleExistingAccountSignupFailure(email);
          }
        }
      } else if (error.error) {
        const errorMessage = error.error.message;
        return { _error: errorMessage };
      } else {
        return { _error: 'Something went wrong' };
      }
    }
    return undefined;
  };

  const handleSignupWithEmail = async (
    formValues: SignupValues,
    requireChallengeResponse: boolean
  ) => {
    const response = await actions.patron.validatePhoneRequest({
      phone: formValues.phone,
      phoneCountryCode: formValues.phoneCountryCode,
    });

    const { payload } = response;
    const potentialError = await handleSignup(
      formValues,
      payload.beautifiedPhone || formValues.phone,
      requireChallengeResponse
    );
    if (potentialError) {
      throw potentialError;
    }
    return potentialError;
  };

  const handleExistingAccountSignupFailure = (email: string) => {
    const checkout = _.includes(location.pathname, '/checkout/') ? '/checkout' : '';
    // fixme: our getPath function is weird, not good type safety.
    const url = getPath(`${checkout}/login?email=` + encodeURIComponent(`${email}`)) as string;
    const message = (
      <span>
        This email is associated with an existing account, but the password was incorrect, please{' '}
        <Anchor href={url}>log in</Anchor>.
      </span>
    );
    return { _error: message, password: ' ' };
  };

  const handleSocialFailure = ({ error }: { error: string | object }) => {
    const errorMessage =
      typeof error === 'string'
        ? error
        : `Unable to login through ${
            queryString.parse(location.search).network || ' a social network'
          }, try a different method`;
    actions.toast.showError(errorMessage);
  };

  const handleSocialSuccess = (
    network: Network,
    accessToken: string,
    profile: OAuthProfile
  ): void => {
    if (tabs[tab] === signupTab) {
      handleSocialSignupSuccess(network, accessToken, profile);
    } else {
      handleSocialLoginSuccess(network, accessToken, profile);
    }
  };

  const handleSocialSignupSuccess = (
    network: Network,
    accessToken: string,
    profile: OAuthProfile
  ) => {
    actions.app
      .createAccountWith(network, accessToken, {
        ...profile,
        tockB2cMarketingOptInStatus: marketingOptInStatus,
      })
      .then(() => {
        window.analytics.sendEvent('Sign-up / Log-in', 'Sign-up', _.capitalize(network));
      })
      .then(() => {
        return handlePostAuth(profile);
      })
      .then(({ payload: { patron } }) => {
        trackAuthEventInternal('Signup', patron || undefined, { network });

        onAuthComplete ? onAuthComplete(tab) : replaceRouteWithFullReload(getNextPath());
      })
      .catch((error: any) => {
        if (safeToNumber(error.error.errorCode) === 2010) {
          handleSocialLoginSuccess(network, accessToken, profile);
        }
      });
  };

  const handleSocialLoginSuccess = (
    network: Network,
    accessToken: string,
    profile: OAuthProfile
  ) => {
    actions.app
      .loginWith(network, accessToken)
      .then(() => {
        window.analytics.sendEvent('Sign-up / Log-in', 'Log-in', _.capitalize(network));
      })
      .then(() => {
        return handlePostAuth(profile);
      })
      .then(({ payload: { patron } }) => {
        trackAuthEventInternal('Login', patron || undefined, { network });

        onAuthComplete ? onAuthComplete(tab) : replaceRouteWithFullReload(getNextPath());
      })
      .catch((error: any) => {
        const errorCode = safeToNumber(error.error?.errorCode);
        if (errorCode === 1001) {
          actions.app
            .createAccountWith(network, accessToken, {
              ...profile,
              tockB2cMarketingOptInStatus: marketingOptInStatus,
            })
            .then(handlePostAuth);
          return;
        } else if (errorCode === 5015) {
          actions.toast.showError(
            `Unable to login. You are logged into the wrong account on ${network}`
          );
        } else if (errorCode === 5007) {
          actions.toast.showError(`This account is locked out from making reservations.`);
        } else {
          actions.toast.showError(`Unable to login through ${network}`);
        }
        // This is an error-handling path, we do not in fact want to do a full page reload should
        // we encounter it.
        replaceRouteWith({
          pathname: _.replace(location.pathname, '/oauth-check', ''),
          search: location.search,
        });
      });
  };

  const getNextPath = () => {
    const continueStr = queryString.parse(location.search).continue;
    return continueStr && isSafeContinuePath(continueStr)
      ? decodeURIComponent(continueStr)
      : (postSigninPath ?? '/');
  };

  const isTwoColumn = isCheckout || isWineShop;

  const query = parse(location.search);
  const { getHeading } = tabs[tab];

  const displayLogin = tabs[tab] === loginTab;
  const displaySignup = tabs[tab] === signupTab && !isLoginOnly;

  const tabsClassName = clsx('Signin-tabs', 'Tabs', {
    'is-checkout': isCheckout,
  });
  const containerClassName = clsx('Signin-container', {
    'is-checkout': isCheckout,
    'is-reschedule': isAccountActivation || isLoginOnly, // handle if only login or don't know what scenario we're in as well, maybe isReschedule?
  });
  const headingContainerClassName = clsx('Signin-headingContainer', {
    'is-checkout': isCheckout,
  });

  const signinSubheadingText = isCheckout
    ? 'Adding your information will create a free Tock account so you can manage your reservation later. '
    : 'Create a free account to book and manage your culinary experiences. ';

  const showTabs = !isAccountActivation && !isLoginOnly;

  return (
    <div>
      {!helmetHeadAlreadyPresent && (
        <Head
          title={`${config?.business?.name || 'Tock'} - ${_.replace(tab, '-', '')}`}
          description="Sign up to create a Tock account to manage your preferences and account details."
          keywords="tock login, tock log in, login, log in, tock sign in, tock signin, sign in, signin, view tock reservations, view tock bookings, tock sign up"
        />
      )}
      <VerifyEmailDialog
        isOpen={showVerifyEmailDialog}
        onClose={onEmailVerificationClose}
        email={userEmail}
      />
      <CredentialsExposedDialog
        isOpen={showCredentialsExposedDialog}
        onClose={() => {
          amplitudeSendEvent(ACTION_CREDENTIALS_EXPOSED_DIALOG_DISMISS);
          setShowCredentialsExposedDialog(false);
          handleAuthComplete();
        }}
        email={userEmail}
      />
      <Wrapper className={containerClassName} size="xsmall">
        <ConsumerOverlay isLoading={isLoading}>
          <div className="Signin-flexContainer">
            {showTabs ? (
              <div className={tabsClassName}>
                <button
                  className={clsx('Tab', { 'is-selected': displayLogin })}
                  disabled={displayLogin}
                  onClick={handleChangeTabsLogin}
                  data-testid="select-signin"
                >
                  <Typography
                    component="span"
                    color={displayLogin ? 'fgPrimaryBase' : 'fgSubtle'}
                    variant="caption"
                  >
                    Log in
                  </Typography>
                </button>

                <button
                  className={clsx('Tab', { 'is-selected': displaySignup })}
                  disabled={displaySignup}
                  onClick={handleChangeTabsSignup}
                >
                  <Typography
                    color={displaySignup ? 'fgPrimaryBase' : 'fgSubtle'}
                    variant="caption"
                    component="span"
                  >
                    Sign up
                  </Typography>
                </button>
              </div>
            ) : null}

            <div className={headingContainerClassName}>
              {(!hideHeader || hideHeader === 'onmobileonly') && (
                <Typography
                  classes={{
                    root:
                      hideHeader === 'onmobileonly'
                        ? styles.signinHeadingHiddenOnMobile
                        : styles.signinHeading,
                  }}
                  align="center"
                  variant="heading02"
                  component="h1"
                >
                  {getHeading({ isCheckout })}
                </Typography>
              )}
              {displaySignup && !isAccountActivation && !isLoginOnly && (
                <div
                  css={(theme: Theme) => [
                    css`
                      margin-top: ${theme.spacing(3)}px;
                    `,
                    // TODO: consider whether the text-align: left here should
                    // just be universal.
                    isWineShop &&
                      css`
                        text-align: left;
                      `,
                  ]}
                  className="Signin-subheading"
                >
                  <Typography variant="body02" component="p">
                    {signinSubheadingText}
                    By creating an account, you agree to our{' '}
                    <Link target="_blank" href="/terms">
                      Terms of Use
                    </Link>{' '}
                    and have read and understand our{' '}
                    <Link target="_blank" href="/privacy">
                      Privacy Policy
                    </Link>
                    . Already have an account?{' '}
                    <Link onClick={handleChangeTabsLogin} data-testid="signin-link">
                      Log&nbsp;in.
                    </Link>
                  </Typography>
                </div>
              )}
            </div>
          </div>
          <div className="Signin-formContainer">
            {displaySignup && showSoftOptInCheckbox ? (
              <div
                css={({ spacing }: Theme) => css`
                  margin: ${spacing(2.5)}px 0;
                `}
              >
                <Checkbox
                  label={SOFT_OPTIN_PROMPT}
                  value={softOptIn}
                  onChange={(event, checked) => setSoftOptIn(checked)}
                />
              </div>
            ) : null}

            <>
              <SocialLoginButtons
                action={tabs[tab].action}
                continuePath={getNextPath()}
                location={location}
                onSocialSuccess={handleSocialSuccess}
                onSocialFailure={handleSocialFailure}
              />
              <FocusedPanelSeparator modifierClassName="Signin-separator" text="OR" />
            </>

            {displayLogin && (
              <LoginForm
                email={query?.email || lastGuest?.email}
                handleChangeTabsSignUp={handleChangeTabsSignup}
                hideSignupLink={isLoginOnly}
                onSubmit={handleLoginWithPassword}
                submitText={submitText}
              />
            )}

            {displaySignup && (
              <>
                <SignupForm
                  layout={isTwoColumn ? 'TWO_COLUMN' : 'SINGLE_COLUMN'}
                  email={query?.email}
                  guest={initialGuest ?? lastGuest}
                  detectedLocation={detectedLocation}
                  onSubmit={handleSignupWithEmail}
                  isAccountActivation={isAccountActivation}
                  submitText={submitText}
                />
                {!isAccountActivation ? (
                  <div className="Signin-loginLinkContainer">
                    <Typography component="span" variant="body02">
                      Already have an account?{' '}
                      <Link onClick={handleChangeTabsLogin} data-testid="signin-link-secondary">
                        Log in
                      </Link>
                    </Typography>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </ConsumerOverlay>
      </Wrapper>
    </div>
  );
};

export default Signin;
